import { importLibrary, setOptions } from '@googlemaps/js-api-loader';
import { create } from 'zustand';

import { GOOGLE_MAPS_API_KEY } from '~/constants';

export type GoogleMapsLibrary = 'places' | 'maps' | 'marker' | 'geocoding';

export enum LibraryStatus {
  Unloaded = 'unloaded',
  Loading = 'loading',
  Loaded = 'loaded',
  Error = 'error',
}

const ALL_LIBRARIES: GoogleMapsLibrary[] = ['places', 'maps', 'marker', 'geocoding'];

setOptions({ key: GOOGLE_MAPS_API_KEY, v: 'weekly' });

interface GoogleMapsStoreState {
  autocompleteSessionToken: google.maps.places.AutocompleteSessionToken | null;
  libraryStatus: Record<GoogleMapsLibrary, LibraryStatus>;
}

interface GoogleMapsStoreAction {
  loadLibrary: (libraries: GoogleMapsLibrary[]) => Promise<void>;
  getSessionToken: () => google.maps.places.AutocompleteSessionToken;
  clearSessionToken: () => void;
}

const initialLibraryStatus = (): Record<GoogleMapsLibrary, LibraryStatus> =>
  ALL_LIBRARIES.reduce(
    (acc, name) => ({ ...acc, [name]: LibraryStatus.Unloaded }),
    {} as Record<GoogleMapsLibrary, LibraryStatus>
  );

export const useGoogleMapsStore = create<GoogleMapsStoreState & GoogleMapsStoreAction>()((set, get) => ({
  autocompleteSessionToken: null,
  libraryStatus: initialLibraryStatus(),

  loadLibrary: async (libraries) => {
    set((state) => {
      const next = { ...state.libraryStatus };
      for (const name of libraries) {
        if (next[name] === LibraryStatus.Unloaded || next[name] === LibraryStatus.Error) {
          next[name] = LibraryStatus.Loading;
        }
      }
      return { libraryStatus: next };
    });

    await Promise.all(
      libraries.map((name) =>
        importLibrary(name).then(
          (lib) => {
            set((state) => ({ libraryStatus: { ...state.libraryStatus, [name]: LibraryStatus.Loaded } }));
            return lib;
          },
          (err) => {
            set((state) => ({ libraryStatus: { ...state.libraryStatus, [name]: LibraryStatus.Error } }));
            throw err;
          }
        )
      )
    );
  },

  getSessionToken: () => {
    const existing = get().autocompleteSessionToken;
    if (existing) return existing;
    const token = new google.maps.places.AutocompleteSessionToken();
    set({ autocompleteSessionToken: token });
    return token;
  },

  clearSessionToken: () => set({ autocompleteSessionToken: null }),
}));
